// suppressionService.js - API client for Suppressions feature
// Handles all suppression-related API calls with authentication

const API_BASE_URL = 'https://driftalarm-functions-bbfmfuh5hmdsfcc7.eastus-01.azurewebsites.net/api';

/**
 * Helper to get auth token (idToken for CIAM)
 */
async function getAuthToken() {
    try {
        const msalInstance = window.msalInstance;
        if (!msalInstance) return null;

        const accounts = msalInstance.getAllAccounts();
        if (accounts.length === 0) return null;

        const request = {
            scopes: ['openid', 'profile', 'email'],
            account: accounts[0],
        };

        const response = await msalInstance.acquireTokenSilent(request);
        return response.idToken;
    } catch (error) {
        console.log('Failed to acquire token:', error.message);
        return null;
    }
}

/**
 * Helper to build headers with auth token
 */
async function getHeaders() {
    const token = await getAuthToken();
    const headers = {
        'Content-Type': 'application/json',
    };

    if (token) {
        headers['Authorization'] = `Bearer ${token}`;
    }

    return headers;
}

/**
 * List all suppressions for the authenticated user
 * @param {object} filters - Optional filters { assetId, status, findingType }
 * @returns {Promise<object>} - { success, suppressions, count, filters }
 */
export async function listSuppressions(filters = {}) {
    try {
        const headers = await getHeaders();
        const params = new URLSearchParams();

        if (filters.assetId) params.append('assetId', filters.assetId);
        if (filters.status) params.append('status', filters.status);
        if (filters.findingType) params.append('findingType', filters.findingType);
        if (filters.bulkBatchId) params.append('bulkBatchId', filters.bulkBatchId);

        const queryString = params.toString();
        const url = `${API_BASE_URL}/suppression-list${queryString ? `?${queryString}` : ''}`;

        const response = await fetch(url, {
            method: 'GET',
            headers,
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.error || `Failed to list suppressions (${response.status})`);
        }

        return await response.json();
    } catch (error) {
        console.error('Error listing suppressions:', error);
        throw error;
    }
}

/**
 * Create a new suppression
 * @param {object} data - Suppression data
 * @param {string} data.assetId - The asset ID
 * @param {string} data.findingType - 'vulnerability', 'port', or 'service'
 * @param {string} data.findingIdentifier - CVE-ID, port number, or template-id
 * @param {string} data.findingName - Human-readable name
 * @param {string} data.reason - Required justification (min 10 chars)
 * @returns {Promise<object>} - { success, message, suppression }
 */
export async function createSuppression(data) {
    try {
        const headers = await getHeaders();

        const response = await fetch(`${API_BASE_URL}/suppression-create`, {
            method: 'POST',
            headers,
            body: JSON.stringify(data),
        });

        const responseData = await response.json().catch(() => ({}));

        if (!response.ok) {
            const error = new Error(responseData.error || `Failed to create suppression (${response.status})`);
            error.statusCode = response.status;
            if (responseData.existingSuppression) {
                error.existingSuppression = responseData.existingSuppression;
            }
            throw error;
        }

        return responseData;
    } catch (error) {
        console.error('Error creating suppression:', error);
        throw error;
    }
}

/**
 * Remove (deactivate) a suppression
 * @param {string} suppressionId - The suppression ID
 * @param {string} reason - Optional reason for removal
 * @returns {Promise<object>} - { success, message, suppression }
 */
export async function removeSuppression(suppressionId, reason = null) {
    try {
        const headers = await getHeaders();

        const response = await fetch(`${API_BASE_URL}/suppression-remove`, {
            method: 'POST',
            headers,
            body: JSON.stringify({ suppressionId, reason }),
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.error || `Failed to remove suppression (${response.status})`);
        }

        return await response.json();
    } catch (error) {
        console.error('Error removing suppression:', error);
        throw error;
    }
}

/**
 * Get audit trail for a suppression
 * @param {string} suppressionId - The suppression ID
 * @returns {Promise<object>} - { success, suppression, auditTrail, count }
 */
export async function getSuppressionAudit(suppressionId) {
    try {
        const headers = await getHeaders();

        const response = await fetch(
            `${API_BASE_URL}/suppression-audit?suppressionId=${suppressionId}`,
            {
                method: 'GET',
                headers,
            }
        );

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.error || `Failed to get suppression audit (${response.status})`);
        }

        return await response.json();
    } catch (error) {
        console.error('Error getting suppression audit:', error);
        throw error;
    }
}

/**
 * Update a suppression (expiration, reason, or disposition)
 * @param {string} suppressionId - The suppression ID
 * @param {object} updates - { expiresAt?, reason?, disposition? }
 * @returns {Promise<object>} - { success, suppression, alarm? }
 */
export async function updateSuppression(suppressionId, updates) {
    try {
        const headers = await getHeaders();

        const response = await fetch(`${API_BASE_URL}/suppression-update/${suppressionId}`, {
            method: 'PUT',
            headers,
            body: JSON.stringify(updates),
        });

        const responseData = await response.json().catch(() => ({}));

        if (!response.ok) {
            throw new Error(responseData.error || `Failed to update suppression (${response.status})`);
        }

        return responseData;
    } catch (error) {
        console.error('Error updating suppression:', error);
        throw error;
    }
}

/**
 * Get suppressions for a specific asset
 * @param {string} assetId - The asset ID
 * @returns {Promise<object>} - { success, suppressions, count }
 */
export async function getAssetSuppressions(assetId) {
    return listSuppressions({ assetId, status: 'active' });
}

// Default export for convenience
const suppressionService = {
    listSuppressions,
    createSuppression,
    updateSuppression,
    removeSuppression,
    getSuppressionAudit,
    getAssetSuppressions,
};

export default suppressionService;
